import { useEffect, useState } from "react";
import { Search, Loader2, Wallet, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const eur = (n) => n == null ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n));

/**
 * Reverse picker: da DocumentDetail permette di collegare il documento
 * a una voce finanziaria esistente.
 */
export default function FinancialItemPickerModal({ open, onClose, onSelect, currentId = null }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [onlyUnattached, setOnlyUnattached] = useState(true);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.financesListItems({});
        setItems(Array.isArray(res) ? res : (res.items || []));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Impossibile caricare le voci");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  if (!open) return null;

  const filtered = items.filter((it) => {
    if (onlyUnattached && it.receipt_document_id && it.receipt_document_id !== currentId) return false;
    if (!q) return true;
    const needle = q.toLowerCase();
    return (it.name || "").toLowerCase().includes(needle)
        || (it.provider_name || "").toLowerCase().includes(needle);
  });

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="fin-item-picker-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="fin-picker-title"
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--pxos-border)" }}>
          <h3 id="fin-picker-title" className="font-display text-xl font-semibold flex items-center gap-2">
            <Link2 className="h-4 w-4" /> Collega a voce finanziaria
          </h3>
          <button type="button" onClick={onClose} aria-label="Chiudi" data-testid="fin-picker-close">✕</button>
        </div>

        <div className="p-4 border-b space-y-2" style={{ borderColor: "var(--pxos-border)" }}>
          <label className="flex items-center gap-2 pxos-input">
            <Search className="h-4 w-4 opacity-60" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per nome o beneficiario…"
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="fin-picker-search"
            />
          </label>
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={onlyUnattached}
              onChange={(e) => setOnlyUnattached(e.target.checked)}
              data-testid="fin-picker-only-unattached"
            />
            Mostra solo voci senza documento allegato
          </label>
        </div>

        <div className="flex-1 overflow-y-auto p-2" data-testid="fin-picker-list">
          {loading ? (
            <div className="p-8 text-center opacity-70 text-sm">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
              Caricamento…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center opacity-70 text-sm">
              Nessuna voce finanziaria trovata.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
              {filtered.map((it) => (
                <li key={it.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(it)}
                    className="w-full text-left px-3 py-3 hover:bg-[var(--pxos-surface-soft)] flex items-start gap-3 rounded-lg"
                    data-testid={`fin-picker-item-${it.id}`}
                  >
                    <Wallet className="h-4 w-4 mt-0.5 opacity-60 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{it.name}</div>
                      <div className="text-[11px] opacity-60 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{eur(it.amount_expected)}</span>
                        {it.due_date && <span>· scad. {new Date(it.due_date).toLocaleDateString("it-IT")}</span>}
                        {it.provider_name && <span>· {it.provider_name}</span>}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-3 border-t flex justify-end" style={{ borderColor: "var(--pxos-border)" }}>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border px-4 py-2 text-xs font-semibold"
            data-testid="fin-picker-cancel"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
